/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from "react";
import { Token } from "../../../store/global/TokenEnum";
import { TOKEN_COLORS_DARK } from "../../../config/tokenColors";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { getMakerTokens } from "../../../store/global/selectors";
import { css } from "@emotion/react";
import clsx from "clsx";

export interface SliderProps {
  visible: boolean;
  symbol: Token;
  value: number;
  maxValue: number;
  isLocked: boolean;
}

export const Slider: React.FC<SliderProps> = props => {
  const tokens = GlobalStore.useState(s => s.tokens);
  const startColor = tokens?.[props.symbol]?.colour ?? TOKEN_COLORS_DARK[props.symbol] ?? "CCC";
  const grayedOutGradient = `${startColor}66`; //RGBA 8digit hex
  const lockedEndColor = `#4D5D5D66`; // If the slider gets locked we gray out everything after the knob
  const endColor = props.isLocked ? lockedEndColor : grayedOutGradient;
  const rangeInputColor = `linear-gradient(to right, ${startColor} 0%, ${startColor} ${props.value}%, rgb(255, 255, 255) 0%, ${endColor} 0%)`;
  const makerTokens = GlobalStore.useState(getMakerTokens);
  const tokenEntry = GlobalStore.useState(s =>
    s.makerTokens.find(entry => entry.token === props.symbol)
  );
  const currentSliderIndex = GlobalStore.useState(s => {
    return s.makerTokens.findIndex(entry => entry.token === tokenEntry?.token);
  });
  const [lastAdjusted, setLastAdjusted] = useState<number>(-1);
  const canUpdateTokensFurther = useCallback(
    (increment: number) => {
      const canIncrement = makerTokens.some((token, index) => {
        return index !== currentSliderIndex && token.pct - increment > 0 && !token.isLocked;
      });
      return canIncrement;
    },
    [currentSliderIndex, makerTokens]
  );

  const getRangeInputThumbColor = useMemo(() => {
    const color = tokens?.[props.symbol]?.colour ?? TOKEN_COLORS_DARK[props.symbol];
    return css`
      &::-webkit-slider-thumb {
        border: 2px solid ${color};
        background: ${color};
      }
      &::-moz-range-thumb {
        border: 2px solid ${color};
        background: ${color};
      }
    `;
  }, [tokens, props.symbol]);

  const getMaxAllowedValue = useCallback(() => {
    const deductables = makerTokens.map(entry => {
      if (entry.isLocked) {
        return entry.pct;
      } else {
        return 1;
      }
    });
    const maxAllowedValue = 101 - deductables.reduce((partialSum, a) => partialSum + a, 0);
    return maxAllowedValue;
  }, [makerTokens]);

  /**
   * On every tick of the slider the function will calculate the delta and redistribute
   * among the rest in a round-robin fashion.The function also keeps track of which slider was
   * adjusted last so that consecutive adjustments are distributed equally.
   */
  const setPercentageSliders = useCallback(
    (value: string) => {
      if (tokenEntry) {
        let newValue = Number(value);
        let remaining = newValue - tokenEntry.pct; // calculate delta
        if (newValue > getMaxAllowedValue()) {
          newValue = getMaxAllowedValue();
        }
        GlobalStore.update(s => {
          s.makerTokens[currentSliderIndex].pct = newValue; //update moved slider
        });
        // Redistribute values in round-robin fashion
        for (
          let i = (lastAdjusted + 1) % makerTokens.length;
          remaining !== 0;
          i = (i + 1) % makerTokens.length
        ) {
          const increment = remaining > 0 ? 1 : -1;
          if (!canUpdateTokensFurther(increment)) break;
          if (i === currentSliderIndex) {
            continue;
          }
          if (makerTokens[i].pct - increment <= 0) continue;
          if (makerTokens[i].isLocked) continue;
          GlobalStore.update(s => {
            if (s.makerTokens[i].pct - increment <= 0) {
              return;
            }
            s.makerTokens[i].pct -= increment;
          });
          remaining -= increment;
          setLastAdjusted(i);
        }
      }
    },
    [
      canUpdateTokensFurther,
      currentSliderIndex,
      getMaxAllowedValue,
      lastAdjusted,
      makerTokens,
      tokenEntry,
    ]
  );
  const handleStartMovingSlider = () => {
    GlobalStore.update(s => {
      s.isMovingSlider = true;
    });
  };
  const handleChangeSliderRelease = () => {
    GlobalStore.update(s => {
      s.isMovingSlider = false;
    });
  };
  if (!props.visible) {
    return null;
  }

  return (
    <div className={"slider-container"}>
      <input
        disabled={props.isLocked}
        type="range"
        placeholder="0.0"
        value={props.value}
        style={{
          background: rangeInputColor,
        }}
        className={clsx({
          "percentage-slider": true,
          "percentage-slider-locked": props.isLocked,
        })}
        css={getRangeInputThumbColor}
        onMouseDown={handleStartMovingSlider}
        onChange={e => setPercentageSliders(e.target.value)}
        onMouseUp={handleChangeSliderRelease}
        onTouchEnd={handleChangeSliderRelease}
        step={1}
        min={1}
        max={100}
        data-testid={`${props.symbol}-slider`}
      />
    </div>
  );
};
